import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { RootState } from 'store';

import { isUserAvailable } from '~weekly-planner/lib/common';
import { extractEvent } from '~weekly-planner/lib/simulate';

import { selectFocusedAppointment, selectFocusedWorkerId, selectOrderBy } from '~weekly-planner/selectors';
import { selectRenderable as selectRenderableClients } from '~weekly-planner/selectors/clients';
import { selectFilterOptions, selectRenderable as selectRenderableWorkers } from '~weekly-planner/selectors/users';
import { selectAllVisualised } from '~weekly-planner/selectors/visualise';

export const selectIsFocusedWorkerInRows = createSelector(
  [selectRenderableWorkers, selectFocusedWorkerId],
  (rows: any[], focusedWorkerId: number | null) => {
    const result = focusedWorkerId ? rows.some(({ id }) => id === focusedWorkerId) : false;
    return result;
  },
);

export const selectWorkerFocusedRows = createSelector(
  [selectRenderableWorkers, selectFocusedWorkerId, selectIsFocusedWorkerInRows],
  (rows, focusedWorkerId, isFocusedWorkerInRows) => {
    let result;
    if (!focusedWorkerId || !isFocusedWorkerInRows) {
      result = rows;
    } else {
      const focusedRow = rows.find((row) => row.id === focusedWorkerId.toString());
      if (!focusedRow) {
        result = rows;
      } else {
        const otherRows = rows.filter((row) => row.id !== focusedWorkerId.toString());
        result = [focusedRow, ...otherRows];
      }
    }
    return result;
  },
);

export const selectAllSimulatedChanges = createSelector(
  [selectRenderableClients, selectRenderableWorkers],
  (clientChanges, workerChanges) => [...clientChanges, ...workerChanges],
);

export const selectFilteredSimulatedWorkers = createSelector(
  selectRenderableWorkers,
  selectFocusedWorkerId,
  selectFocusedAppointment,
  selectFilterOptions,
  selectOrderBy,
  (rows, focusedWorkerId, focused, filterOptions, orderBy) => {
    if (!filterOptions || Object.values(filterOptions).every((value) => value === null)) {
      if (!orderBy.availability && !orderBy.distance) {
        return rows;
      }
    }

    const filteredRows = rows.filter((row) => {
      const matchesFilters = Object.values(filterOptions)
        .filter((ids) => Array.isArray(ids))
        .every((ids) => ids.includes(row.id));
      return matchesFilters || row.id === focusedWorkerId?.toString();
    });

    if (orderBy.availability) {
      const { date, start_time, end_time } = focused?.appointment || {};
      filteredRows.sort((a, b) => {
        const aAvailable = date
          ? isUserAvailable(
              a.availability?.[dayjs(date).format('dddd') as keyof typeof a.availability] ?? {},
              start_time,
              end_time,
            )
          : false;
        const bAvailable = date
          ? isUserAvailable(
              b.availability?.[dayjs(date).format('dddd') as keyof typeof b.availability] ?? {},
              start_time,
              end_time,
            )
          : false;

        if (aAvailable && !bAvailable) return -1;
        if (!aAvailable && bAvailable) return 1;

        return a.full_name.localeCompare(b.full_name);
      });
    }

    // Ensure that focused worker remains at the top of the sort regardless of filters
    if (focusedWorkerId) {
      filteredRows.sort((a, b) => {
        if (a.id === focusedWorkerId.toString()) return -1;
        if (b.id === focusedWorkerId.toString()) return 1;
        return 0;
      });
    }

    return filteredRows;
  },
);

export const selectMergedVisualisedData = createSelector(
  [selectFilteredSimulatedWorkers, selectAllVisualised],
  (simulatedRows, visualisedRows) => {
    // Create a map to hold workers by ID for fast lookup
    const workerMap = new Map();

    // First add all simulated rows to the map
    simulatedRows.forEach((worker) => {
      workerMap.set(worker.id, worker);
    });

    // Then merge any visualised rows, overriding duplicate IDs
    visualisedRows.forEach((worker) => {
      if (workerMap.has(worker.id)) {
        // If worker already exists, only update the events
        const existingWorker = workerMap.get(worker.id);
        workerMap.set(worker.id, {
          ...existingWorker,
          events: {
            ...existingWorker.events,
            ...worker.events,
          },
        });
      } else {
        // If this is a new worker, add it to the map
        workerMap.set(worker.id, worker);
      }
    });

    // Convert the map back to an array
    return Array.from(workerMap.values());
  },
);

export const selectInteracting = createSelector(
  [selectRenderableWorkers, (state: RootState) => state.weeklyPlanner.unsaved.interacting],
  (rows, key) => {
    const result = key ? (extractEvent(rows, key) ?? null) : null;
    return result;
  },
);

export const selectAllLoading = (state: RootState) =>
  state.weeklyPlanner.unallocated.loading === 'pending' ||
  state.weeklyPlanner.awardAlerts.loading === 'pending' ||
  state.weeklyPlanner.conflicts.loading === 'pending' ||
  state.weeklyPlanner.missingQualifications.loading === 'pending' ||
  state.weeklyPlanner.rejected.loading === 'pending' ||
  state.weeklyPlanner.users.loading === 'pending';

export const selectAllErrors = createSelector(
  (state: RootState) => state.weeklyPlanner,
  (wp) => [
    wp.unallocated.error,
    wp.awardAlerts.error,
    wp.conflicts.error,
    wp.missingQualifications.error,
    wp.rejected.error,
    wp.users.error,
    wp.weeklyPlanner.error,
  ],
);
export const selectRequiredRebuild = (state: RootState) => state.weeklyPlanner.weeklyPlanner.optionRequiresRebuild;
export const selectUnallocatedLoading = (state: RootState) => state.weeklyPlanner.unallocated.loading;
